import React, { useCallback, useState, useRef, createElement } from 'react'
import {
  Download,
  Upload,
  ArrowLeft,
  X,
  Sparkles,
  Copy,
  Check,
} from 'lucide-react'
import { toPng } from 'html-to-image'
import Cropper from 'react-easy-crop'
import type { Template } from './SocialMediaTemplateTypes'
interface SocialPostGeneratorProps {
  'data-id'?: string
  templates: Template[]
  initialQuote?: string
  initialAuthor?: string
  initialImage?: string | null
}
type Format = 'square' | 'portrait' | 'story'
interface CropArea {
  x: number
  y: number
  width: number
  height: number
}
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.src = url
  })
const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: CropArea,
): Promise<string> => {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('No 2d context')
  }
  canvas.width = pixelCrop.width
  canvas.height = pixelCrop.height
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  )
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (blob) {
        const reader = new FileReader()
        reader.readAsDataURL(blob)
        reader.onloadend = () => {
          resolve(reader.result as string)
        }
      }
    }, 'image/jpeg')
  })
}
const getResponsiveClasses = (format: Format) => {
  const isStory = format === 'story'
  const aspectClass =
    format === 'square'
      ? 'aspect-square'
      : format === 'portrait'
        ? 'aspect-[4/5]'
        : 'aspect-[9/16]'
  const textSizeClass =
    format === 'square'
      ? 'text-3xl'
      : format === 'portrait'
        ? 'text-4xl'
        : 'text-4xl sm:text-5xl'
  const authorSizeClass =
    format === 'square'
      ? 'text-lg'
      : format === 'portrait'
        ? 'text-xl'
        : 'text-2xl'
  return {
    aspectClass,
    textSizeClass,
    authorSizeClass,
    isStory,
  }
}
export const SocialPostGenerator = ({
  'data-id': dataId,
  templates,
  initialQuote = '',
  initialAuthor = '',
  initialImage = null,
}: SocialPostGeneratorProps) => {
  const [step, setStep] = useState<'template' | 'format' | 'customize'>(
    'template',
  )
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  )
  const [selectedFormat, setSelectedFormat] = useState<Format | null>(null)
  const [quote, setQuote] = useState(initialQuote)
  const [author, setAuthor] = useState(initialAuthor)
  const [caption, setCaption] = useState('')
  const [customImage, setCustomImage] = useState<string | null>(initialImage)
  const [autoFitText, setAutoFitText] = useState(true)
  const [showCropModal, setShowCropModal] = useState(false)
  const [imageToCrop, setImageToCrop] = useState<string | null>(null)
  const [crop, setCrop] = useState({
    x: 0,
    y: 0,
  })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(
    null,
  )
  // AI Quote Generation Modal State
  const [showAIModal, setShowAIModal] = useState(false)
  const [aiPrompt, setAIPrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [aiError, setAIError] = useState<string | null>(null)
  // Caption copy state
  const [captionCopied, setCaptionCopied] = useState(false)
  const postRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const getResponsiveClassesWithAutoFit = (format: Format) => {
    const baseClasses = getResponsiveClasses(format)
    if (!autoFitText) {
      return baseClasses
    }
    // Calculate text size based on quote length
    const quoteLength = quote.length
    let textSizeClass = baseClasses.textSizeClass
    let authorSizeClass = baseClasses.authorSizeClass
    // Much more aggressive auto-fit with granular sizing
    if (format === 'square') {
      if (quoteLength > 200) {
        textSizeClass = 'text-lg'
        authorSizeClass = 'text-xs'
      } else if (quoteLength > 150) {
        textSizeClass = 'text-xl'
        authorSizeClass = 'text-sm'
      } else if (quoteLength > 100) {
        textSizeClass = 'text-2xl'
        authorSizeClass = 'text-base'
      } else if (quoteLength > 60) {
        textSizeClass = 'text-2xl'
        authorSizeClass = 'text-base'
      }
    } else if (format === 'portrait') {
      if (quoteLength > 250) {
        textSizeClass = 'text-xl'
        authorSizeClass = 'text-sm'
      } else if (quoteLength > 200) {
        textSizeClass = 'text-2xl'
        authorSizeClass = 'text-base'
      } else if (quoteLength > 150) {
        textSizeClass = 'text-3xl'
        authorSizeClass = 'text-lg'
      } else if (quoteLength > 100) {
        textSizeClass = 'text-3xl'
        authorSizeClass = 'text-lg'
      }
    } else if (format === 'story') {
      // Story is narrow, so text wraps more - need aggressive sizing
      if (quoteLength > 200) {
        textSizeClass = 'text-lg'
        authorSizeClass = 'text-xs'
      } else if (quoteLength > 150) {
        textSizeClass = 'text-xl'
        authorSizeClass = 'text-sm'
      } else if (quoteLength > 100) {
        textSizeClass = 'text-2xl'
        authorSizeClass = 'text-base'
      } else if (quoteLength > 70) {
        textSizeClass = 'text-3xl'
        authorSizeClass = 'text-lg'
      }
    }
    return {
      ...baseClasses,
      textSizeClass,
      authorSizeClass,
    }
  }
  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template)
    setStep('format')
  }
  const handleFormatSelect = (format: Format) => {
    setSelectedFormat(format)
    setStep('customize')
  }
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        const imageDataUrl = event.target?.result as string
        setImageToCrop(imageDataUrl)
        setShowCropModal(true)
      }
      reader.readAsDataURL(file)
    }
  }
  const onCropComplete = useCallback(
    (croppedArea: any, croppedAreaPixels: CropArea) => {
      setCroppedAreaPixels(croppedAreaPixels)
    },
    [],
  )
  const handleCropApply = async () => {
    if (imageToCrop && croppedAreaPixels) {
      try {
        const croppedImage = await getCroppedImg(imageToCrop, croppedAreaPixels)
        setCustomImage(croppedImage)
        setShowCropModal(false)
        setImageToCrop(null)
        setCrop({
          x: 0,
          y: 0,
        })
        setZoom(1)
      } catch (error) {
        console.error('Error cropping image:', error)
      }
    }
  }
  const handleCropCancel = () => {
    setShowCropModal(false)
    setImageToCrop(null)
    setCrop({
      x: 0,
      y: 0,
    })
    setZoom(1)
  }
  const handleExport = async () => {
    if (postRef.current) {
      try {
        const dataUrl = await toPng(postRef.current, {
          quality: 0.95,
          pixelRatio: 2,
          cacheBust: true,
          filter: (node) => true,
        })
        const link = document.createElement('a')
        link.download = `post-${selectedFormat}-${selectedTemplate?.id}-${Date.now()}.png`
        link.href = dataUrl
        link.click()
      } catch (error) {
        console.error('Error exporting image:', error)
        try {
          const dataUrl = await toPng(postRef.current, {
            quality: 0.9,
            pixelRatio: 1,
            cacheBust: false,
          })
          const link = document.createElement('a')
          link.download = `post-${selectedFormat}-${selectedTemplate?.id}-${Date.now()}.png`
          link.href = dataUrl
          link.click()
        } catch (retryError) {
          console.error('Retry export failed:', retryError)
          alert('Failed to export image. Please try again.')
        }
      }
    }
  }
  const getCropAspect = () => {
    if (!selectedFormat) return 1
    if (selectedFormat === 'square') return 1
    if (selectedFormat === 'portrait') return 4 / 5
    return 9 / 16
  }
  const imageUrl = customImage || selectedTemplate?.getDefaultImage?.() || null
  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      setAIError('Please describe the kind of quote you want first.')
      return
    }
    setIsGenerating(true)
    setAIError(null)
    try {
      const response = await fetch('/api/ai/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: aiPrompt.trim() }),
      })
      const payload = await response.json().catch(() => null)
      if (!response.ok || !payload) {
        const message = payload?.error || 'Unable to generate content right now.'
        throw new Error(message)
      }
      const rawResult = typeof payload.result === 'string' ? payload.result.trim() : ''
      if (!rawResult) {
        throw new Error('AI response was empty. Please try again.')
      }
      let structured
      try {
        structured = JSON.parse(rawResult)
      } catch (error) {
        throw new Error('AI response format was invalid. Please try again.')
      }
      setQuote(structured.quote || '')
      setAuthor(structured.author || '')
      setCaption(structured.caption || '')
      setShowAIModal(false)
      setAIPrompt('')
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Something went wrong. Please try again.'
      setAIError(message)
    } finally {
      setIsGenerating(false)
    }
  }
  const handleAIModalClose = () => {
    setShowAIModal(false)
    setAIPrompt('')
    setAIError(null)
  }
  const handleCopyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption)
      setCaptionCopied(true)
      setTimeout(() => setCaptionCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy caption:', error)
    }
  }
  return (
    <div className="w-full max-w-6xl mx-auto" data-id={dataId}>
      {/* AI Generation Modal */}
      {showAIModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="bg-white rounded-lg w-full max-w-2xl mx-4 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <Sparkles className="text-blue-600" size={24} />
                <h3 className="font-[var(--heading-font)] text-xl text-gray-800">
                  Generate Quote with AI
                </h3>
              </div>
              <button
                onClick={handleAIModalClose}
                className="text-gray-500 hover:text-gray-700"
                disabled={isGenerating}
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Describe the type of quote you want
                </label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAIPrompt(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                  rows={4}
                  placeholder="E.g., 'A motivational quote about success' or 'An inspirational quote about peace and mindfulness'"
                  disabled={isGenerating}
                />
                {aiError && (
                  <p className="text-sm text-red-600 mt-2">{aiError}</p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Try keywords like: motivation, peace, love, wisdom, change,
                  strength, happiness, journey, dream, time
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleAIModalClose}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                  disabled={isGenerating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAIGenerate}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!aiPrompt.trim() || isGenerating}
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} />
                      Generate Quote
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Crop Modal */}
      {showCropModal && imageToCrop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-[var(--heading-font)] text-xl text-gray-800">
                Adjust Your Image
              </h3>
              <button
                onClick={handleCropCancel}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            <div className="relative h-96 bg-gray-100">
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={getCropAspect()}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zoom
                </label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={handleCropCancel}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCropApply}
                  className="px-6 py-2 bg-[var(--accent-color)] text-white rounded-md hover:bg-[var(--accent-color-hover)] transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Step 1: Template Selection */}
      {step === 'template' && (
        <div>
          <h2 className="font-[var(--heading-font)] text-3xl mb-4 text-gray-800">
            Step 1: Choose Your Template
          </h2>
          <p className="text-gray-600 mb-8">
            Select a design style for your post
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateSelect(template)}
                className="border-2 border-gray-200 rounded-lg hover:border-[var(--accent-color)] hover:shadow-lg transition-all text-left overflow-hidden group"
              >
                <div className="w-full">{template.renderPreview()}</div>
                <div className="p-4 bg-white group-hover:bg-gray-50 transition-colors">
                  <h3 className="font-[var(--heading-font)] text-lg mb-1 text-gray-800">
                    {template.name}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {template.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Step 2: Format Selection */}
      {step === 'format' && (
        <div>
          <button
            onClick={() => setStep('template')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6"
          >
            <ArrowLeft size={20} />
            Back to templates
          </button>
          <h2 className="font-[var(--heading-font)] text-3xl mb-4 text-gray-800">
            Step 2: Choose Your Format
          </h2>
          <p className="text-gray-600 mb-8">
            Select the size for your social media post
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <button
              onClick={() => handleFormatSelect('square')}
              className="p-8 border-2 border-gray-200 rounded-lg hover:border-[var(--accent-color)] hover:bg-gray-50 transition-all"
            >
              <div className="aspect-square w-32 mx-auto mb-4 bg-gray-200 rounded"></div>
              <h3 className="font-[var(--heading-font)] text-xl mb-2 text-gray-800">
                Square
              </h3>
              <p className="text-gray-600 text-sm">1080 x 1080px</p>
              <p className="text-gray-500 text-xs mt-2">Classic feed post</p>
            </button>
            <button
              onClick={() => handleFormatSelect('portrait')}
              className="p-8 border-2 border-gray-200 rounded-lg hover:border-[var(--accent-color)] hover:bg-gray-50 transition-all"
            >
              <div className="aspect-[4/5] w-32 mx-auto mb-4 bg-gray-200 rounded"></div>
              <h3 className="font-[var(--heading-font)] text-xl mb-2 text-gray-800">
                Portrait
              </h3>
              <p className="text-gray-600 text-sm">1080 x 1350px</p>
              <p className="text-gray-500 text-xs mt-2">Taller feed post</p>
            </button>
            <button
              onClick={() => handleFormatSelect('story')}
              className="p-8 border-2 border-gray-200 rounded-lg hover:border-[var(--accent-color)] hover:bg-gray-50 transition-all"
            >
              <div className="aspect-[9/16] w-20 mx-auto mb-4 bg-gray-200 rounded"></div>
              <h3 className="font-[var(--heading-font)] text-xl mb-2 text-gray-800">
                Story
              </h3>
              <p className="text-gray-600 text-sm">1080 x 1920px</p>
              <p className="text-gray-500 text-xs mt-2">Full-screen story</p>
            </button>
          </div>
        </div>
      )}
      {/* Step 3: Customize */}
      {step === 'customize' && selectedTemplate && selectedFormat && (
        <div>
          <button
            onClick={() => setStep('format')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6"
          >
            <ArrowLeft size={20} />
            Back to format
          </button>
          <h2 className="font-[var(--heading-font)] text-3xl mb-4 text-gray-800">
            Step 3: Customize Your Post
          </h2>
          <p className="text-gray-600 mb-8">
            Add your quote and personalize the design
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Form */}
            <div className="space-y-6">
              {/* AI Generate Button */}
              <button
                onClick={() => {
                  setAIError(null)
                  setShowAIModal(true)
                }}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-base font-medium"
              >
                <Sparkles size={20} />
                Generate Quote with AI
              </button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    Or enter manually
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quote
                </label>
                <textarea
                  value={quote}
                  onChange={(e) => setQuote(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                  rows={4}
                  placeholder="Enter your inspirational quote..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {quote.length} characters
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Author
                </label>
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                  placeholder="Author name..."
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Caption (with hashtags)
                  </label>
                  <button
                    onClick={handleCopyCaption}
                    disabled={!caption.trim()}
                    className="flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {captionCopied ? (
                      <>
                        <Check size={14} className="text-green-600" />
                        <span className="text-green-600">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy size={14} />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                  rows={6}
                  placeholder="Enter your caption with hashtags..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  {caption.length} characters
                </p>
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoFitText}
                    onChange={(e) => setAutoFitText(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Auto-fit text size
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Automatically adjust text size to fit longer quotes
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Image (Optional)
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-gray-400 text-gray-700 bg-white rounded-md hover:bg-gray-50 hover:border-gray-500 transition-colors"
                  >
                    <Upload size={18} />
                    {customImage ? 'Change Image' : 'Upload Image'}
                  </button>
                  {customImage && (
                    <button
                      onClick={() => {
                        setImageToCrop(customImage)
                        setShowCropModal(true)
                      }}
                      className="px-4 py-2 border-2 border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors text-sm"
                    >
                      Adjust Image
                    </button>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                {customImage && (
                  <p className="text-sm text-gray-600 mt-2">
                    ✓ Custom image uploaded
                  </p>
                )}
              </div>
              <button
                onClick={handleExport}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium shadow-sm"
              >
                <Download size={20} />
                Export Post
              </button>
            </div>
            {/* Right: Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Live Preview
              </label>
              <div className="flex justify-center">
                <div
                  ref={postRef}
                  className={
                    selectedFormat === 'story'
                      ? 'w-full max-w-xs'
                      : 'w-full max-w-md'
                  }
                >
                  {selectedTemplate.renderPost({
                    quote,
                    author,
                    imageUrl,
                    format: selectedFormat,
                    ...getResponsiveClassesWithAutoFit(selectedFormat),
                    hasAuthor: author.trim().length > 0,
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
